import React, { useState, useRef, useEffect } from 'react';
import '../../../../../../../Assets/styles/LandingStyles/LandingComponentsStyles/BeginnersGuideStyles/SudokuBasicsStyles/SudokuBasicsTutorial.css';
import { useLanguage } from 'Core/Translations/LanguageContext';

export default function TutorialStep3({ stepNumber = 3 }: { stepNumber?: number }) {
    const { t } = useLanguage();
    const localStorageKey = `tutorial-step-${stepNumber}-answer`;
    const [selectedNumber, setSelectedNumber] = useState<number | null>(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);

    // Focus the input when editing starts
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditing]);

    // Load cached answer on mount
    useEffect(() => {
        const cached = localStorage.getItem(localStorageKey);
        if (cached !== null) {
            const parsed = parseInt(cached, 10);
            if (!isNaN(parsed)) {
                setSelectedNumber(parsed);
                setIsCorrect(parsed === 4); // Correct answer is 4
                setShowFeedback(true);
            }
        }
    }, [localStorageKey]);

    // 4x4 grid data with missing number at position D2 (row 3, column 1)
    const gridData = [
        [2, 1, 3, 4],
        [null, 3, 2, null],
        [1, 2, null, null],
        [null, null, null, null] // Target cell: row index 3, col index 1
    ];

    // Modified finalizeInput to accept an optional value parameter.
    const finalizeInput = (value?: number | null) => {
        const finalNumber = value !== undefined ? value : selectedNumber;
        if (finalNumber !== null) {
            // Clamp the value between 0 and 9
            const finalValue = Math.min(9, Math.max(0, finalNumber));
            setSelectedNumber(finalValue);
            const correct = finalValue === 4; // Correct answer is 4
            setIsCorrect(correct);
            setShowFeedback(true);
            // Save the answer to localStorage
            localStorage.setItem(localStorageKey, String(finalValue));
        } else {
            setSelectedNumber(null);
            setShowFeedback(false);
            localStorage.removeItem(localStorageKey);
        }
        setIsEditing(false);
    };

    // Updated handleInputChange to auto-finalize after one digit is entered
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value === '') {
            setSelectedNumber(null);
            return;
        }
        const parsedNumber = parseInt(value, 10);
        if (!isNaN(parsedNumber)) {
            setSelectedNumber(parsedNumber);
            if (value.length === 1) {
                finalizeInput(parsedNumber);
            }
        } else {
            setSelectedNumber(null);
            finalizeInput(null);
        }
    };

    const handleInputBlur = () => {
        const value = inputRef.current?.value;
        finalizeInput(value ? parseInt(value, 10) : null);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Allow only numeric inputs (0-9) and control keys
        if (!/[0-9]|Backspace|Delete|ArrowLeft|ArrowRight|Tab/.test(e.key)) {
            e.preventDefault();
        }
        if (e.key === 'Enter') {
            e.currentTarget.blur();
        }
    };

    // Handle cell click on the target cell
    const handleCellClick = () => {
        if (isCorrect) return; // Disable click if the answer is already correct
        // Clear previous selection and feedback if any
        if (selectedNumber !== null) {
            setSelectedNumber(null);
            setShowFeedback(false);
            localStorage.removeItem(localStorageKey);
        }
        setIsEditing(true);
    };

    // Handler for option buttons as an alternative input method
    const handleOptionClick = (num: number) => {
        if (isCorrect) return;
        // Clamp the value between 0 and 9
        const finalValue = Math.min(9, Math.max(0, num));
        setSelectedNumber(finalValue);
        setIsEditing(false);
        const correct = finalValue === 4;
        setIsCorrect(correct);
        setShowFeedback(true);
        localStorage.setItem(localStorageKey, String(finalValue));
    };

    // Compute dynamic class names for the target cell (row 3, col 1)
    const targetCellClassName = selectedNumber === null
        ? "cell target-cell empty"
        : `cell target-cell selected-cell ${isCorrect ? 'correct' : 'incorrect'}`;

    // Compute dynamic class for input when editing
    const inputClassName = selectedNumber === null
        ? "cell-input big-input empty"
        : `cell-input big-input selected-cell ${isCorrect ? 'correct' : 'incorrect'}`;

    return (
        <div className="tutorial-step-container">
            <div className="content-container">
                <div className="grid-wrapper">
                    <div className="sudoku-grid-4x4">
                        {gridData.map((row, rowIndex) =>
                            row.map((num, colIndex) => {
                                // Identify the target cell at row 3, col 1
                                if (rowIndex === 3 && colIndex === 1) {
                                    return (
                                        <div
                                            key={`${rowIndex}-${colIndex}`}
                                            className={targetCellClassName}
                                            onClick={handleCellClick}
                                        >
                                            {isEditing ? (
                                                <input
                                                    type="number"
                                                    ref={inputRef}
                                                    value={selectedNumber !== null ? selectedNumber : ''}
                                                    onChange={handleInputChange}
                                                    onBlur={handleInputBlur}
                                                    onKeyDown={handleInputKeyDown}
                                                    className={inputClassName}
                                                    min="0"
                                                    max="9"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    style={{ width: '100%', height: '100%' }}
                                                />
                                            ) : (
                                                selectedNumber !== null ? selectedNumber : ''
                                            )}
                                        </div>
                                    );
                                }
                                return (
                                    <div
                                        key={`${rowIndex}-${colIndex}`}
                                        className={`cell ${num === null ? 'empty' : ''}`}
                                    >
                                        {num !== null ? num : ''}
                                    </div>
                                );
                            })
                        )}
                    </div>
                    <span className="grid-size-label">{t.beginnersGuide.origins.gridSize.four}</span>
                </div>

                <div className="text-content">
                    <div className="header-container-tutorial">
                        <h2 className="header-tutorial">{t.beginnersGuide.basics.fillMissing}</h2>
                        {showFeedback && (
                            <span className={`status-text ${isCorrect ? 'correct' : 'incorrect'}`}>
                                {isCorrect ? t.beginnersGuide.basics.correct : t.beginnersGuide.basics.incorrect}
                            </span>
                        )}
                    </div>
                    <p className="instructions">
                        {t.beginnersGuide.basics.basicsStep3.instructions}
                    </p>

                    <div className="options-row">
                        {[1, 3, 4, 2].map((num, index) => {
                            let indicatorClass = "option-indicator";
                            let buttonClass = "option-button";

                            if (showFeedback && selectedNumber === num) {
                                if (num === 4) {
                                    indicatorClass += " correct";
                                    buttonClass += " correct";
                                } else {
                                    indicatorClass += " incorrect";
                                    buttonClass += " selected-wrong";
                                }
                            }

                            return (
                                <div className="option-container" key={num}>
                                    <button
                                        className={buttonClass}
                                        onClick={() => handleOptionClick(num)}
                                        disabled={isCorrect}
                                    >
                                        {num}
                                    </button>
                                    <div className={indicatorClass} />
                                    <span className="option-label">
                                        {String.fromCharCode(65 + index)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {showFeedback && (
                <div className={`feedback-message ${isCorrect ? 'success' : 'error'}`}>
                    {isCorrect ? t.beginnersGuide.basics.basicsStep3.feedbackCorrect : t.beginnersGuide.basics.basicsStep3.feedbackIncorrect}
                </div>
            )}
        </div>
    );
}